import { useMemo, useState, type ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  useApplyPlanLibraryPlan,
  usePlanLibraryPlans,
} from 'hooks/planLibrary/planLibraryHooks';
import { useExactIncident } from 'components/IAP/hooks/incidentHooks';
import {
  PlanExtrasPreview,
  PlanPickerList,
  planHasContents,
} from 'components/MapPage/CorporateViewTabs/PlanLibraryTab/PlanPicker';

export function ApplyPlanBody({
  incidentId,
  positionId,
  positionName,
  onApplied,
  footer,
}: {
  incidentId: string;
  /** When set, every created task is assigned to this roster seat. */
  positionId?: string;
  positionName?: string;
  onApplied?: () => void;
  /** Optional footer wrapper (dialog vs page). Receives the Apply / Cancel controls. */
  footer?: (controls: {
    applyDisabled: boolean;
    applyPending: boolean;
    onApply: () => void;
  }) => ReactNode;
}) {
  const { data: plans = [], isLoading } = usePlanLibraryPlans();
  const applyPlan = useApplyPlanLibraryPlan();
  const { data: incidentInfo } = useExactIncident(incidentId);
  const incident = useMemo(
    () => ({
      categories: incidentInfo?.incident_types,
      aorIds: incidentInfo?.areas_of_responsibility,
    }),
    [incidentInfo?.incident_types, incidentInfo?.areas_of_responsibility]
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = useMemo(
    () => plans.find((plan) => plan.id === selectedId) ?? null,
    [plans, selectedId]
  );

  const applyDisabled =
    !selected || !planHasContents(selected) || applyPlan.isPending;

  const onApply = () => {
    if (!selected) return;
    applyPlan.mutate(
      { id: selected.id, incidentId, positionId },
      { onSuccess: () => onApplied?.() }
    );
  };

  const description = positionId
    ? `Bring the plan’s tasks, ICS forms, documents, and roster templates into this workspace. Every task is assigned to ${positionName || 'this position'}. Applying clones content authored on the plan’s template workspace.`
    : 'Select a plan to clone into this workspace—tasks, ICS forms, documents, and roster structure from the plan’s template. Tasks keep the ICS positions saved on each task.';

  return (
    <div className="tw:flex tw:min-h-0 tw:flex-col tw:gap-3">
      <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">{description}</p>

      {isLoading ? (
        <p className="tw:text-sm tw:text-muted-foreground">Loading plans…</p>
      ) : plans.length === 0 ? (
        <p className="tw:text-sm tw:text-muted-foreground">
          No plans in the library yet. Create one from Hub → Plan Library.
        </p>
      ) : (
        <div className="tw:flex tw:min-h-0 tw:flex-col tw:gap-3">
          <PlanPickerList
            className="tw:max-h-64"
            plans={plans}
            incident={incident}
            selectedId={selectedId}
            onSelect={(plan) => setSelectedId(plan.id)}
          />

          {selected ? (
            !planHasContents(selected) ? (
              <p className="tw:text-sm tw:text-muted-foreground">
                This plan has no template tasks, ICS forms, documents, or roster
                templates yet.
              </p>
            ) : (
              <div className="tw:flex tw:flex-col tw:gap-2">
                <PlanExtrasPreview plan={selected} />
                {selected.tasks.length > 0 ? (
                  <ul className="tw:max-h-40 tw:overflow-auto tw:rounded-md tw:border tw:border-border tw:p-2">
                    {selected.tasks.map((task) => (
                      <li
                        key={task.id}
                        className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:px-1 tw:py-1.5"
                      >
                        <span className="tw:min-w-0 tw:flex-1 tw:text-sm tw:break-words">
                          {task.name}
                        </span>
                        <span className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
                          {task.status ? (
                            <Badge variant="outline">{task.status}</Badge>
                          ) : null}
                          <Badge variant="outline">{task.priority}</Badge>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {(selected.forms ?? []).length > 0 ? (
                  <ul className="tw:max-h-32 tw:overflow-auto tw:rounded-md tw:border tw:border-border tw:p-2">
                    {(selected.forms ?? []).map((form) => (
                      <li
                        key={form.id}
                        className="tw:px-1 tw:py-1.5 tw:text-sm"
                      >
                        {form.label}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )
          ) : (
            <p className="tw:text-sm tw:text-muted-foreground">
              Select a plan to preview its tasks and ICS forms.
            </p>
          )}
        </div>
      )}

      {footer ? (
        footer({
          applyDisabled,
          applyPending: applyPlan.isPending,
          onApply,
        })
      ) : (
        <div className="tw:flex tw:justify-end tw:gap-2">
          <Button
            type="button"
            disabled={applyDisabled}
            onClick={onApply}
          >
            {applyPlan.isPending ? 'Applying…' : 'Apply plan'}
          </Button>
        </div>
      )}
    </div>
  );
}
